<template>
  <MeetingsEntryPoint v-if="!isMeetingsEnabled" @enabled="meetingsRefetch" />

  <template v-else>
    <Teleport to="#meetings-tab-actions">
      <button class="btn-primary-color-s" :disabled="saving" @click="handleSave">
        {{ saving ? t('common.saving') : t('common.saveChanges') }}
      </button>
    </Teleport>

    <div class="flex gap-8 py-6">
      <!-- Left: Availability config -->
      <div class="min-w-0 flex-1 overflow-hidden">
        <div class="mb-6 max-w-[500px]">
          <h2 class="txt-headline-xl">{{ t('Meetings.availability.title') }}</h2>
          <i18n-t keypath="Meetings.availability.subtitle" tag="p" class="txt-body-m">
            <template #duration>
              <strong>{{ t('Meetings.availability.slotDuration') }}</strong>
            </template>
          </i18n-t>
        </div>

        <CircleSpinner v-if="loading || eventLoading" />

        <template v-else>
          <!-- Day tabs -->
          <div class="mb-4 flex gap-2">
            <button
              v-for="(day, idx) in eventDays"
              :key="day.date"
              class="rounded-full px-3 py-1.5 text-body-s-bold transition-colors"
              :class="
                selectedDay === day.date
                  ? 'bg-primary-main text-inverse'
                  : 'border border-main bg-surface text-main hover:bg-surface-elevation-1'
              "
              @click="selectedDay = day.date"
            >
              {{ t('Meetings.availability.dayLabel', { date: formatDate(day.date), dayNumber: idx + 1 }) }}
            </button>
          </div>

          <!-- Availability Grid -->
          <MeetingsAvailabilityGrid v-model="currentDayGrid" :time-slots="timeSlotLabels" />

          <!-- Description Section -->
          <div class="mt-8 mb-8">
            <h2 class="txt-headline-xl">{{ t('Meetings.availability.description.title') }}</h2>
            <p class="mt-1 txt-body-m-regular">{{ t('Meetings.availability.description.subtitle') }}</p>

            <div class="mt-4 space-y-4">
              <TextInput
                v-model="descriptionDe"
                type="textarea"
                :max-length="200"
                :label="t('Meetings.availability.description.labelDe')"
                :placeholder="t('Meetings.availability.description.placeholderDe')"
              />
              <TextInput
                v-model="descriptionEn"
                type="textarea"
                :max-length="200"
                :label="t('Meetings.availability.description.labelEn')"
                :placeholder="t('Meetings.availability.description.placeholderEn')"
              />
            </div>
          </div>
        </template>

        <MeetingsConfigurationWarningsDialog :show="showWarningsDialog" :warnings="pendingWarnings" @confirm="handleWarningsConfirm" />

        <MeetingsConfigurationDeleteDialog :show="showDeleteDialog" @confirm="handleDeleteConfirm" />
      </div>

      <!-- Right: Preview -->
      <div class="w-[400px] shrink-0">
        <h2 class="mb-4 txt-headline-xl">{{ t('Meetings.availability.preview.title') }}</h2>
        <div class="top-4 rounded-lg border border-subtle bg-surface p-6">
          <MeetingSlotPicker
            v-if="previewSlots.length > 0"
            :title="t('Meetings.availability.preview.bookingTitle')"
            :description="previewDescription"
            :slot-duration-minutes="30"
            :slots="previewSlots"
            :locale="locale"
          />
          <p v-else class="text-body-m text-subtle">{{ t('Meetings.availability.preview.empty') }}</p>
        </div>
      </div>
    </div>
  </template>
</template>

<script setup lang="ts">
import { CircleSpinner } from '@ramp106/omrjs-core-ui'
import { TextInput } from '@ramp106/omrjs-core-ui/deprecated'
import { MeetingSlotPicker } from '@ramp106/omrjs-events-ui'
import type { ComputedRef } from 'vue'
import { inject, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { MeetingsConfigurationDeleteDialog, MeetingsConfigurationWarningsDialog } from '@/components/Meetings'
import { useMeetingsConfiguration } from '@/composables/meetings/useMeetingsConfiguration'

import MeetingsAvailabilityGrid from './components/MeetingsAvailabilityGrid.vue'
import MeetingsEntryPoint from './MeetingsEntryPoint.vue'

const { t, locale } = useI18n()

const showToast = inject<(message: string, type: 'success' | 'error') => void>('showToast', () => {})
const isMeetingsEnabled = inject<ComputedRef<boolean>>('meetingsEnabled')!
const meetingsRefetch = inject<() => void>('meetingsRefetch')!

const {
  saving,
  loading,
  eventLoading,
  eventDays,
  descriptionDe,
  descriptionEn,
  selectedDay,
  currentDayGrid,
  timeSlotLabels,
  previewSlots,
  previewDescription,
  formatDate,
  checkForWarnings,
  performSave,
  resetLocalState,
} = useMeetingsConfiguration()

// Dialog state
const showWarningsDialog = ref(false)
const pendingWarnings = ref<string[]>([])
const showDeleteDialog = ref(false)

function handleDeleteConfirm(_confirmed: boolean) {
  showDeleteDialog.value = false
}

async function handleSave() {
  saving.value = true
  try {
    const warnings = await checkForWarnings()
    if (warnings.length > 0) {
      pendingWarnings.value = warnings
      showWarningsDialog.value = true
      saving.value = false
      return
    }
    await performSave()
  } catch (_error) {
    showToast(t('common.errorOccurred'), 'error')
    saving.value = false
  }
}

async function handleWarningsConfirm(confirmed: boolean) {
  showWarningsDialog.value = false
  pendingWarnings.value = []

  if (confirmed) {
    saving.value = true
    try {
      await performSave()
    } catch (_error) {
      showToast(t('common.errorOccurred'), 'error')
      saving.value = false
    }
  } else {
    resetLocalState()
  }
}
</script>
